/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ApiStandardResponse,
    AxiosPromiseFn,
    DEVELOPMENT,
    IDecodedJwt,
    IFilterOptions,
    IJwtPayload,
    JEST_WORKER_ID,
    NodeEnvironmentType,
    PRODUCTION,
    TEST,
    TEST_E2E,
    V1_DOMAIN_ISSUERS,
    V2_DOMAIN_ISSUERS,
} from '@quadminds/shared/commons';

import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { Request as ExpressRequest } from 'express';

/**
 * Wraps axios call and returns a standard API response at succeed or handles error
 * @param {axiosFn} func
 * @return {Promise<ApiStandardResponse<T>>} promise with meta and data attributes
 */
export async function axiosStandardApiResolver<T>(func: AxiosPromiseFn<T>): Promise<ApiStandardResponse<T>> {
    try {
        const response = await func();
        return response.data;
    } catch (err: unknown) {
        throw handleRequestError(err);
    }
}

/**
 * Wraps axios call and returns actual data at succeed or handles error
 *  * @return {Promise<T>} promise of desired data
 * @param {axiosFn} func
 */
export async function axiosResolver<T>(func: () => Promise<AxiosResponse<T>>): Promise<T> {
    try {
        const response = await func();
        return response.data;
    } catch (err: unknown) {
        throw handleRequestError(err);
    }
}

/**
 * It logs the error to the console and returns the error
 * @param {any} err The error object that was thrown.
 * @returns The error object.
 */
export const handleRequestError = (err: unknown) => {
    if (isTestRunner()) return; // Avoid logging in test runner
    if (isAxiosError(err)) {
        console.error('Request Failed:', err.config);
        if (err.response) {
            console.error('Status:', err.response.status);
            console.error('Data:', err.response.data);
            console.error('Headers:', err.response.headers);
        }
    } else if (err instanceof Error) {
        console.error('Different error than Axios. Message: ', err.message);
    }
    return err;
};

export const formatHttpError = (error: unknown): string => {
    const getCircularReplacer = () => {
        const seen = new WeakSet();
        return (key: string, value: any) => {
            if (typeof value === 'object' && value !== null) {
                if (seen.has(value)) {
                    return '[Circular]';
                }
                seen.add(value);
            }
            return value;
        };
    };

    if (!isAxiosError(error)) {
        return error instanceof Error ? error.message : JSON.stringify(error, getCircularReplacer());
    }
    if (error.response) {
        return `Status: ${error.response.status} Data: ${JSON.stringify(error.response.data, getCircularReplacer())}`;
    } else if (error.request) {
        return `Request was made but no response was received.`;
    } else {
        return error.message ?? JSON.stringify(error, getCircularReplacer());
    }
};

export const isDevelopment = () => !process.env?.['NODE_ENV'] || process.env?.['NODE_ENV'] === DEVELOPMENT;

export const isTest = (nodeEnv?: NodeEnvironmentType) =>
    nodeEnv === TEST || process.env?.['NODE_ENV'] === TEST || isTestCloudRun();

export const isProduction = (nodeEnv?: NodeEnvironmentType) =>
    nodeEnv === PRODUCTION || process.env?.['NODE_ENV'] === PRODUCTION || isProductionCloudRun();

export const isUnitTest = () => process.env?.['NODE_ENV'] === TEST && process.env?.[JEST_WORKER_ID]?.length;

export const isTestEndToEnd = () => process.env?.['NODE_ENV'] === TEST_E2E;

export const isTestRunner = (nodeEnv?: NodeEnvironmentType) =>
    [TEST_E2E].includes(nodeEnv ?? (process.env?.['NODE_ENV'] as string)) || isUnitTest();

export const isDeployedEnvironment = (nodeEnv?: NodeEnvironmentType) =>
    [TEST, PRODUCTION].includes(nodeEnv ?? (process.env?.['NODE_ENV'] as string)) ||
    Boolean(getGoogleCloudServiceName());

export const getGoogleCloudServiceName = () => {
    const env = process.env;
    return env['K_SERVICE'];
};

export const isTestCloudRun = () => getGoogleCloudServiceName()?.includes(TEST);

export const isProductionCloudRun = () => getGoogleCloudServiceName()?.includes(PRODUCTION);

export const isCypress = () => process.env?.['NX_CYPRESS'] === 'true';

export const envSuffix = () => (isTest() || isDevelopment() ? 'test' : 'prod');

export const hasAccessToken = () => {
    const token = localStorage.getItem('token');
    return Boolean(token);
};

/**
 * Decodes a JWT token and returns the decoded information.
 * @param {string | null | undefined} t - The JWT token to decode.
 * @return {IDecodedJwt} An object containing the decoded JWT information.
 */
export const jwtDecode = (t: string | null | undefined): IDecodedJwt => {
    const tokenAux: IDecodedJwt = {
        raw: '',
        header: { alg: '' },
        payload: {} as IJwtPayload,
    };
    if (!t) return tokenAux;
    tokenAux.raw = t;
    if (t.split('.').length > 1) {
        tokenAux.header = JSON.parse(window.atob(t.split('.')[0]));
        tokenAux.payload = JSON.parse(window.atob(t.split('.')[1]));
    }

    return tokenAux;
};

/**
 * It takes a decoded JWT as an argument and returns true if the token's expiration date is in the
 * future
 * @param {IDecodedJwt} decodedToke - IDecodedJwt - This is the decoded token that we get from the
 * jwt.verify() method.
 * @returns {boolean} A boolean value.
 */
export const validateTokenExp = (decodedToke: IDecodedJwt) => {
    return decodedToke.payload.exp * 1000 > Date.now();
};

// ---- Typescript type guard fns
export function isString(test: any): test is string {
    return typeof test === 'string';
}

export function isNumber(value: number | string): value is number {
    return typeof value === 'number';
}

export function isDefined<T>(val: T | undefined | null): val is T {
    return val !== undefined && val !== null;
}

const isObject = (a: unknown): a is Record<string, unknown> => a instanceof Object;

export function isPropertyDefined<T extends object, K extends keyof T>(
    obj: T,
    prop: K,
): obj is T & Record<K, Exclude<T[K], undefined>> {
    return prop in obj;
}

// Custom implementation to avoid injecting axios dependency
const isAxiosError = (err: unknown): err is AxiosError => {
    return (err as AxiosError).isAxiosError;
};

// ---- End of typescript type guards

/**
 *  Method used to extract jwt token from either "authorization" or "Authorization" headers
 *  Returns token string from 'Bearer x' auth type.
 *  Can be used for both client side Axios requests or server side Express requests
 * @param req
 * @returns string | undefined
 */
export const getTokenFromAuthHeaders = (req: Partial<AxiosRequestConfig> | ExpressRequest) => {
    if (!req.headers) return;
    const authHeaderValue = req.headers['Authorization'] || req.headers['authorization'];
    if (!isString(authHeaderValue)) return;
    const token = authHeaderValue ? authHeaderValue.replace('Bearer ', '') : '';
    return token;
};

/**
 * Utility that translates js object to mongo query with dot notation
 * e.g `{ key: { nestedKey: 'value' } }` => `{ 'key.nestedKey': 'value' }`
 */
export const parseMongoDbQuery = (query: any) => {
    const parsedQuery: Record<string, any> = {};
    for (const key in query) {
        if (isObject(query[key])) {
            // use recursion to build dot notation query
            const subQuery = parseMongoDbQuery(query[key]);
            for (const subKey in subQuery) {
                if (!subKey.startsWith('$')) {
                    parsedQuery[`${key}.${subKey}`] = subQuery[subKey];
                } else {
                    // if subKey starts with $ it may be a query like $eq, $lt, $gt
                    // so we let the query stay the same
                    parsedQuery[key] = query[key];
                }
            }
        } else {
            // not an object
            parsedQuery[key] = query[key];
        }
    }
    return parsedQuery;
};

/**
 * Merges a current filter and a new filter, combining $or clauses into an $and if both have $or.
 * If both have $or, the result will only have $and (no top-level $or).
 * @param currentFilter The existing filter object.
 * @param newFilter The new filter object to merge.
 * @returns The merged filter object.
 */
export function mergeOrFilters(
    currentFilter: Record<string, unknown>,
    newFilter: Record<string, unknown>,
): Record<string, unknown> {
    const hasOrInCurrent = !!currentFilter['$or'];
    const hasOrInNew = !!newFilter['$or'];

    if (hasOrInCurrent && hasOrInNew) {
        // Remove $or from both filters and return only $and
        const { $or: orCurrent, ...restCurrent } = currentFilter;
        const { $or: orNew, ...restNew } = newFilter;
        return {
            $and: [
                { ...restCurrent, $or: orCurrent },
                { ...restNew, $or: orNew },
            ],
        };
    }

    // If only one has $or, just merge normally (shallow)
    return { ...currentFilter, ...newFilter };
}

export const clearTokensFromStorage = (tokenName?: string, refreshTokenName?: string) => {
    localStorage.removeItem(tokenName ?? 'token');
    localStorage.removeItem(refreshTokenName ?? 'refreshToken');
};

export function getQueryParamsWithFilters(params?: IFilterOptions): string {
    if (!params) return '';
    const parsedFilters = {
        ...(params ?? {}),
        filter: params?.filter ? JSON.stringify(params.filter) : undefined,
    };
    let query = getQueryParams(parsedFilters as unknown as Record<string, string>);
    if (query) query = `?${query}`;

    return query;
}

/** Returns stringified query params without `?`. If no params, returns empty string */
export const getQueryParams = (params: Record<string, string>): string => {
    for (const key in params) {
        if (typeof params[key] === 'undefined' || params[key] === '') {
            delete params[key];
        }
    }
    return new URLSearchParams(params).toString();
};

export function generateMetaResponse<T>(data: T): ApiStandardResponse<T> {
    if (!Array.isArray(data)) {
        throw new Error('Argument must be an array');
    }
    return {
        meta: { total: data.length },
        data: data,
    };
}
export const getSubdomain = (url: string) => {
    let domain = url;
    if (url.includes('://')) {
        [, domain] = url.split('://');
    }
    const [subdomain] = domain.split('.');
    return subdomain;
};

/**
 * It logs the error to the console and returns the error
 * @param {any} err The error object that was thrown.
 * @returns The error object.
 */
export const handleError = (file: string, err: unknown) => {
    const name = (err as Error).name;
    const message = (err as Error).message;

    console.error({ file, error: { name, message } });
};

export function deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key)) {
            return false;
        }

        if (!deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

/**
 * The function checks if the user's operating system has a dark color scheme preference.
 */
export const isDarkByOS = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export const sanitizedKeys = <T>(obj: T): T => {
    return Object.keys(obj as unknown as object).reduce((acc, cur) => {
        if (obj[cur as keyof T] !== undefined) {
            acc[cur as keyof T] = obj[cur as keyof T];
        }
        return acc;
    }, {} as T);
};

/**
 * Validates whether a given string is a valid URL.
 *
 * @param {string} string - The string to be validated.
 * @return {boolean} True if the string is a valid URL, false otherwise.
 */
export function isValidUrl(string: string) {
    try {
        new URL(string);
        return true;
    } catch (err) {
        return false;
    }
}
/**
 * Retrieves the payload from a JWT token.
 *
 * @param {string} token - The JWT token.
 * @return {IJwtPayload} The payload extracted from the token.
 */
export function getTokenPayload(token: string): IJwtPayload {
    return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
}

/**
 * Formats a URL to a WebSocket URL.
 *
 * @param {string} url - The URL to be formatted.
 * @return {string} The formatted WebSocket URL.
 */
export function formatUrlToWebSocket(url: string) {
    if (url.startsWith('http://')) {
        return 'ws://' + url.slice(7);
    } else if (url.startsWith('https://')) {
        return 'wss://' + url.slice(8);
    } else {
        return 'ws://' + url;
    }
}

export function isNotEmptyObject(obj: any): boolean {
    return obj !== null && typeof obj === 'object' && Object.keys(obj).length > 0;
}

export function getRandomValueFromArray<T>(arr: T[]): T | undefined {
    if (arr.length === 0) {
        return undefined;
    }

    const randomIndex = Math.floor(Math.random() * arr.length);
    return arr[randomIndex];
}

export function getNestedProperty<T>(obj: Record<string, unknown>, propName: string): T | undefined {
    // Safe guard. Return upon undefined object and propName index
    if (!obj || typeof obj !== 'object' || !propName) return undefined;

    const propParts = propName.split('.');
    let currentObj: unknown = obj;

    for (const prop of propParts) {
        if (!currentObj || typeof currentObj !== 'object') {
            return undefined;
        }

        currentObj = (currentObj as Record<string, unknown>)[prop];
    }

    return currentObj as T;
}

export async function withFallback<T, K>(
    primaryFn: () => Promise<T | null>,
    fallbackFn: () => Promise<K | null>,
): Promise<T | K | null> {
    try {
        const result = await primaryFn();
        return result ?? (await fallbackFn());
    } catch (error) {
        return await fallbackFn();
    }
}

// Can be used for testing purposes
export const paginatedResponseMock = <T>(aMock: T) => ({
    data: Array.isArray(aMock) ? aMock : [aMock],
    meta: { total: Array.isArray(aMock) ? aMock.length : 1 },
});

/**
 * It takes a encoded or decoded JWT payload as an argument and returns the token version based on issuer
 * @param {string | IJwtPayload} token - The decoded token payload or token to be decoded.
 * @returns {string}  v1 or v2. Error if the issuer is invalid
 */
export const getAuthVersion = (token: IJwtPayload | string) => {
    let decodeToken: IJwtPayload;
    if (typeof token === 'string') {
        decodeToken = getTokenPayload(token as string);
    } else {
        decodeToken = token;
    }

    if (V1_DOMAIN_ISSUERS.includes(decodeToken.iss as typeof V1_DOMAIN_ISSUERS[number])) return 'V1';
    if (V2_DOMAIN_ISSUERS.includes(decodeToken.iss as typeof V2_DOMAIN_ISSUERS[number])) return 'V2';

    throw new Error('Invalid issuer');
};

/**
 * A simple sleep function that returns a promise that resolves after a given number of milliseconds.
 * @param {number} ms - The number of milliseconds to wait before resolving the promise.
 * @returns {Promise<void>} A promise that resolves after the given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export function convertToUpperSnakeCase(input: string): string {
    // Reemplaza las letras minúsculas seguidas de mayúsculas por un guión bajo seguido de la letra en mayúscula
    const snakeCase = input.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
    return snakeCase;
}

export async function withRateLimit<T>(promise: Promise<T>, maxRetries = 3): Promise<T> {
    let retries = 0;
    const DEFAULT_WAIT_TIME_MS = 2000;
    const attempt: () => Promise<T> = async () => {
        try {
            const response = await promise;
            return response;
        } catch (error) {
            if (isAxiosError(error)) {
                if (error.response?.status === 429) {
                    if (retries < maxRetries) {
                        const headers = error.response.headers;
                        const retryAfter =
                            headers['retry-after'] ||
                            headers['Retry-After'] ||
                            headers['retry-after-short'] ||
                            headers['x-ratelimit-reset-short'] ||
                            headers['x-ratelimit-reset-medium'] ||
                            headers['x-ratelimit-reset-long'];

                        const waitTime = retryAfter ? Number(retryAfter) * 1000 : DEFAULT_WAIT_TIME_MS;
                        await new Promise((resolve) => setTimeout(resolve, waitTime));
                        retries++;
                        return attempt();
                    }
                    console.error('Rate limit exceeded. No more retries will be performed.');
                }
            }
            throw error;
        }
    };

    return attempt();
}

export async function withRetry<T>(func: () => Promise<T>, maxRetries: number): Promise<T> {
    let attempt = 0;

    while (attempt < maxRetries) {
        try {
            attempt++;
            return await func();
        } catch (error) {
            console.error(`Attempt ${attempt} failed:`, error);

            if (attempt >= maxRetries) throw error;
        }
    }

    throw new Error('Execution failed after retries');
}

export function deepMerge<T extends Record<string, any>>(target: T, source: Record<string, any>): T {
    const result = { ...target } as any;

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const sourceValue = source[key];
            const targetValue = result[key];

            if (
                isObject(sourceValue) &&
                isObject(targetValue) &&
                !Array.isArray(sourceValue) &&
                !Array.isArray(targetValue)
            ) {
                result[key] = deepMerge(targetValue, sourceValue);
            } else {
                result[key] = sourceValue;
            }
        }
    }

    return result;
}

// ---- Locale Utilities for i18n
/**
 * Converts locale format from 'en_US.UTF-8' to 'en-US'
 * @param locale - The locale string to convert
 * @returns Converted locale in language-region format
 */
export const convertLocaleFormat = (locale: string): string => {
    const [languageRegion] = locale.split('.');
    const [language, region] = languageRegion.split('_');
    return region ? `${language}-${region}` : language;
};

/**
 * Converts locale format from 'en_US.UTF-8' to 'en' (removes region)
 * @param locale - The locale string to convert
 * @returns Language code without region
 */
export const convertLocaleFormatWithoutRegion = (locale: string): string => {
    const [languageRegion] = locale.split('.');
    const [language] = languageRegion.split('_');
    return language;
};

/**
 * Normalizes language codes to match i18next supported languages
 * Converts various locale formats to standardized language codes
 * @param lang - The language/locale string to normalize
 * @param supportedLanguages - Array of supported language codes (default: ['es', 'en'])
 * @param fallbackLanguage - Fallback language if no match found (default: 'es')
 * @returns Normalized language code
 */
export const normalizeLanguageForI18n = (
    lang: string,
    supportedLanguages: string[] = ['es', 'en'],
    fallbackLanguage = 'es',
): string => {
    console.log('[normalizeLanguageForI18n] Input:', { lang, supportedLanguages, fallbackLanguage });

    if (!lang) {
        console.log('[normalizeLanguageForI18n] No lang provided, returning fallback:', fallbackLanguage);
        return fallbackLanguage;
    }

    // Convert to lowercase for comparison
    const normalizedInput = lang.toLowerCase();
    console.log('[normalizeLanguageForI18n] Normalized input:', normalizedInput);

    // Direct match check
    if (supportedLanguages.includes(normalizedInput)) {
        console.log('[normalizeLanguageForI18n] Direct match found:', normalizedInput);
        return normalizedInput;
    }

    // Extract base language from various formats
    const baseLanguage = convertLocaleFormatWithoutRegion(lang).toLowerCase();
    console.log('[normalizeLanguageForI18n] Base language extracted:', baseLanguage);

    // Special handling for Spanish variants
    if (baseLanguage === 'es') {
        console.log('[normalizeLanguageForI18n] Spanish detected, returning es');
        return 'es';
    }

    // Handle English variants
    if (baseLanguage === 'en') {
        console.log('[normalizeLanguageForI18n] English detected, returning en');
        return 'en';
    }

    // Try to find a match in supported languages
    const match = supportedLanguages.find((supported) => supported.toLowerCase().startsWith(baseLanguage));
    console.log('[normalizeLanguageForI18n] Match found:', match);

    const result = match || fallbackLanguage;
    console.log('[normalizeLanguageForI18n] Final result:', result);
    return result;
};

/**
 * Gets the browser's preferred language normalized for i18n
 * @param supportedLanguages - Array of supported language codes
 * @param fallbackLanguage - Fallback language if browser language not supported
 * @returns Normalized browser language
 */
export const getBrowserLanguageForI18n = (
    supportedLanguages: string[] = ['es', 'en'],
    fallbackLanguage = 'es',
): string => {
    if (typeof window === 'undefined') return fallbackLanguage;

    const browserLang = window.navigator.language || window.navigator.languages?.[0];
    return normalizeLanguageForI18n(browserLang, supportedLanguages, fallbackLanguage);
};

/**
 * Extracts and normalizes language from user object for i18n
 * @param userLocale - User's locale string (e.g., from user.locale)
 * @param supportedLanguages - Array of supported language codes
 * @param fallbackLanguage - Fallback language
 * @returns Normalized language code for i18n
 */
export const getUserLanguageForI18n = (
    userLocale?: string,
    supportedLanguages: string[] = ['es', 'en'],
    fallbackLanguage = 'es',
): string => {
    if (!userLocale) return fallbackLanguage;
    return normalizeLanguageForI18n(userLocale, supportedLanguages, fallbackLanguage);
};
